import dataDictionary from '../assets/data-dictionary/dataDictionary.json';
import {
  parseId,
  getAttributeById,
  myDataZoneType,
} from '../helpers/dataDictionary';
import {
  addLeafToGroup,
  addLeafToRoot,
  createLeaf,
  createMembershipLeaf,
  findLeafByVariableId,
  findMembershipLeaf,
  findNodeById,
  isLeaf,
  isMembershipLeaf,
  isVariableLeaf,
  membershipLeafId,
  moveLeafIntoGroup,
  removeLeavesByCategoryKey,
  removeLeavesByVariableId,
  removeNodeById,
  toggleNodeOperator,
  ungroupById,
  updateLeafVariableState,
  updateModelLeafRange,
  wrapNodeWithFreshLeaf,
  wrapNodesInGroup,
} from '../helpers/expressionTree';
import type {
  ExpressionNode,
  MembershipLeaf,
  MembershipResourceType,
  VariableState,
} from '../types/dataDictionary';

export const TYPES = {
  VARIABLE_ATTRIBUTE: 'variable-attribute',
  MY_DATA_ATTRIBUTE: 'my-data-attribute',
  // PR 2b-3: existing leaves (cards already in the dropzone) are draggable
  // for drag-to-group / drag-into-group gestures. They carry the leaf's
  // nodeId + the source zoneType so cross-zone drops can be rejected.
  EXPRESSION_LEAF: 'expression-leaf',
};

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------
// Both variable filters AND audience/file membership filters live in two
// recursive expression trees — `expression` for the Include zone and
// `excludeExpression` for the Exclude zone. Each tree is null when its
// zone is empty. PR 2c retired the separate `myDataInclude`/`myDataExclude`
// arrays — membership is now leaves with `kind: 'audience'` or `'file'`
// inside the same tree as variable leaves, drag-and-droppable into groups,
// togglable with AND/OR.

export type BuilderState = {
  expression: ExpressionNode | null;
  excludeExpression: ExpressionNode | null;
  audienceId: string | null;
  audienceName: string;
  audienceNameError: boolean;
  includeOnlyOneRecordPerHousehold: boolean;
  hasStateChanged: boolean;
  hasGottenCounts: boolean;
  recordsAvailable: number;
  recordsIncluded: number;
  incomingRecordsIncluded?: number;
  emailsAvailable?: number;
  price: number;
  adjustmentError: string | null;
  isSavedAudience: boolean;
};

export const initialState: BuilderState = {
  expression: null,
  excludeExpression: null,
  audienceId: null,
  audienceName: 'Untitled Audience',
  audienceNameError: false,
  includeOnlyOneRecordPerHousehold: false,
  hasStateChanged: false,
  hasGottenCounts: false,
  recordsAvailable: 0,
  recordsIncluded: 0,
  price: 0,
  adjustmentError: null,
  isSavedAudience: false,
};

// ---------------------------------------------------------------------------
// Action type names (unchanged from PR1 — payload semantics are unchanged for
// the UI, only how state is stored under the hood differs)
// ---------------------------------------------------------------------------

export const RESET = 'reset';
export const ADD_ATTRIBUTE = 'add-attribute';
export const REMOVE_CATEGORY = 'remove-category';
export const REMOVE_ATTRIBUTE = 'remove-attribute';
export const SET_ATTRIBUTE_STATE = 'set-attribute-state';
export const ADD_MY_DATA_RESOURCE = 'add-my-data-resource';
export const REMOVE_MY_DATA_RESOURCE = 'remove-my-data-resource';
export const REMOVE_MY_DATA_CATEGORY = 'remove-my-data-category';
export const SET_MY_DATA_RESOURCE_STATE = 'set-my-data-resource-state';
export const AUDIENCE_NAME_CHANGE = 'audience-name-change';
export const CHECKBOX_CHANGE = 'checkbox-change';
export const LOAD_SAVED_AUDIENCE = 'load-saved-audience';
export const SET_RECORDS_AVAILABLE = 'set-records-available';
export const SET_RECORDS_INCLUDED = 'set-records-included';
export const SET_SAVED_AUDIENCE = 'set-saved-audience';
export const SET_AUDIENCE_NAME_ERROR = 'set-audience-name-error';
export const SET_ADJUSTMENT_ERROR = 'set-adjustment-error';

// PR 2b-2 — boolean expression actions
export const TOGGLE_OPERATOR = 'toggle-operator';
export const WRAP_IN_GROUP = 'wrap-in-group';
export const UNGROUP = 'ungroup';

// PR 2b-3 — drag-to-group gestures
//
// ADD_ATTRIBUTE_AND_WRAP — a fresh attribute from the data dictionary is
//   dropped on top of an existing card. New leaf is created and the target
//   is replaced with a 2-child OR (or AND) group containing both.
// ADD_LEAF_TO_GROUP — a fresh attribute is dropped on an existing group
//   container. New leaf is appended as a sibling of the group's children.
// MOVE_LEAF_INTO_GROUP — an existing leaf (already in the tree) is dragged
//   onto an existing group container. The leaf moves there.
export const ADD_ATTRIBUTE_AND_WRAP = 'add-attribute-and-wrap';
export const ADD_LEAF_TO_GROUP = 'add-leaf-to-group';
export const MOVE_LEAF_INTO_GROUP = 'move-leaf-into-group';

// PR 2c — drag-to-group gestures for AUDIENCE/FILE membership leaves.
// ADD_MY_DATA_AND_WRAP — a fresh audience/file from the sidebar is dropped
//   on a card. New membership leaf wraps with the target into a group.
// ADD_MY_DATA_TO_GROUP — a fresh audience/file is dropped on a group
//   container. New membership leaf is appended.
export const ADD_MY_DATA_AND_WRAP = 'add-my-data-and-wrap';
export const ADD_MY_DATA_TO_GROUP = 'add-my-data-to-group';

// AND/OR feature tour — seed the Include zone with a canned expression so
// every step of the tour has a real anchor to attach to. Only fires when the
// Include zone is empty; pre-existing user work is left untouched (the
// caller is responsible for that check).
export const LOAD_TOUR_SAMPLE = 'load-tour-sample';

// ---------------------------------------------------------------------------
// Helpers — bridge zoneType ('include' | 'exclude') to expression key
// ---------------------------------------------------------------------------

type ZoneType = 'include' | 'exclude';

function expressionKeyFor(zoneType: ZoneType): 'expression' | 'excludeExpression' {
  return zoneType === 'include' ? 'expression' : 'excludeExpression';
}

/**
 * ATAD-146: createMembershipLeaf throws on a resource type it doesn't know
 * (it used to make a seed-file leaf of it). A bad drop is ignored with a
 * warning, like an unknown attribute in ADD_ATTRIBUTE, rather than crashing
 * the builder mid-render.
 */
function membershipLeafOrNull(
  type: MembershipResourceType,
  resource: { id: string; name: string },
): MembershipLeaf | null {
  try {
    return createMembershipLeaf(type, resource);
  } catch (err) {
    console.warn('[builderReducer]', (err as Error).message);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action = { type: string; payload?: any };

export const reducer = (state: BuilderState, action: Action): BuilderState => {
  const { type, payload } = action;

  switch (type) {
    case ADD_ATTRIBUTE: {
      const { id, zoneType } = payload as { id: string; zoneType: ZoneType };
      const attributeToAdd = getAttributeById(dataDictionary, id) as VariableState | undefined;
      if (!attributeToAdd) {
        console.warn('[builderReducer] attributeToAdd is undefined for id:', id);
        return state;
      }

      const exprKey = expressionKeyFor(zoneType);
      const leaf = createLeaf(attributeToAdd);
      return {
        ...state,
        [exprKey]: addLeafToRoot(state[exprKey], leaf),
        hasStateChanged: true,
      };
    }

    case REMOVE_CATEGORY: {
      const { key: category, zoneType } = payload as { key: string; zoneType: ZoneType };
      const exprKey = expressionKeyFor(zoneType);
      return {
        ...state,
        [exprKey]: removeLeavesByCategoryKey(state[exprKey], category),
        hasStateChanged: true,
      };
    }

    case REMOVE_ATTRIBUTE: {
      const { id, zoneType } = payload as { id: string; zoneType: ZoneType };
      const exprKey = expressionKeyFor(zoneType);
      return {
        ...state,
        [exprKey]: removeLeavesByVariableId(state[exprKey], id),
        hasStateChanged: true,
      };
    }

    case SET_ATTRIBUTE_STATE: {
      const { id, zoneType, state: valueState, loadingInitialState } = payload as {
        id: string;
        zoneType: ZoneType;
        state: VariableState['state'];
        loadingInitialState?: boolean;
      };
      const exprKey = expressionKeyFor(zoneType);
      const tree = state[exprKey];
      // No-op when the leaf isn't present (mirrors legacy behavior).
      if (!findLeafByVariableId(tree, id)) {
        return {
          ...state,
          hasStateChanged: loadingInitialState ? state.hasStateChanged : true,
        };
      }
      return {
        ...state,
        [exprKey]: updateLeafVariableState(tree, id, valueState),
        hasStateChanged: loadingInitialState ? state.hasStateChanged : true,
      };
    }

    case ADD_MY_DATA_RESOURCE: {
      // PR 2c: audiences/files are now leaves in the expression tree
      // (no more myDataInclude/myDataExclude arrays). Build a membership
      // leaf for the dropped resource and append it at the root.
      const { id, name, type: resourceType, zoneType } = payload as {
        id: string;
        name: string;
        type: MembershipResourceType;
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      const leaf = membershipLeafOrNull(resourceType, { id, name });
      if (!leaf) return state;
      return {
        ...state,
        [exprKey]: addLeafToRoot(state[exprKey], leaf),
        hasStateChanged: true,
      };
    }

    case REMOVE_MY_DATA_RESOURCE: {
      const { id, type: resourceType, zoneType, nodeId } = payload as {
        id: string;
        type: MembershipResourceType;
        zoneType: ZoneType;
        /** ATAD-146: the card's own node, when the card knows it. */
        nodeId?: string;
      };
      const exprKey = expressionKeyFor(zoneType);
      // ATAD-146: a card that sends its nodeId removes exactly itself — the
      // same model can sit in a zone twice with different score ranges. The
      // node must still be this resource's leaf; otherwise fall back to the
      // first leaf for the resource, as before.
      const node = nodeId ? findNodeById(state[exprKey], nodeId) : null;
      const found =
        node &&
        isLeaf(node) &&
        isMembershipLeaf(node) &&
        node.kind === resourceType &&
        membershipLeafId(node) === id
          ? node
          : findMembershipLeaf(state[exprKey], resourceType, id);
      if (!found) return state;
      return {
        ...state,
        [exprKey]: removeNodeById(state[exprKey], found.nodeId),
        hasStateChanged: true,
      };
    }

    case REMOVE_MY_DATA_CATEGORY: {
      // Remove every membership leaf of the given resource type. Used by
      // the (now-deprecated) left-side "delete all files" / "delete all
      // audiences" controls. Keeps working post-PR-2c on the tree shape.
      const { type: resourceType, zoneType } = payload as {
        type: MembershipResourceType;
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      const tree = state[exprKey];
      if (!tree) return state;
      // Walk and collect matching leaves, then remove by nodeId.
      const leaves = (function collect(node): any[] {
        if (!node) return [];
        if (node.type === 'leaf') {
          const match = isMembershipLeaf(node) && node.kind === resourceType;
          return match ? [node] : [];
        }
        return node.children.flatMap(collect);
      })(tree);
      let next: ExpressionNode | null = tree;
      for (const leaf of leaves) {
        next = removeNodeById(next, leaf.nodeId);
      }
      if (next === tree) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case SET_MY_DATA_RESOURCE_STATE: {
      // ATAD-146: a model leaf's score range, written by ModelCard's Range.
      // Model leaves are the only membership leaves with state — audience,
      // file and ZIP leaves are just an id + name — so anything else (or a
      // nodeId no longer in the tree) leaves the state untouched.
      //
      // Range reports its starting value on mount with loadingInitialState,
      // and a loaded saved audience must not read as edited. A report that
      // doesn't change the range (a blur without an edit) changes nothing.
      const { nodeId, zoneType, state: range, loadingInitialState } = payload as {
        nodeId: string;
        zoneType: ZoneType;
        state: { min?: number; max?: number } | null;
        loadingInitialState?: boolean;
      };
      const exprKey = expressionKeyFor(zoneType);
      const tree = state[exprKey];
      const next = updateModelLeafRange(tree, nodeId, range);
      if (next === tree) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: loadingInitialState ? state.hasStateChanged : true,
      };
    }

    case AUDIENCE_NAME_CHANGE: {
      const { value } = payload;
      // ATAD-127: trim here rather than at each egress point. This action
      // is dispatched on blur (not per keystroke), so trimming can't fight
      // the user mid-word, and it keeps the name the builder holds
      // identical to the one the server persists — surrounding whitespace
      // used to survive into the Order and orphan it from its Audience.
      return {
        ...state,
        audienceName: typeof value === 'string' ? value.trim() : value,
      };
    }

    case CHECKBOX_CHANGE: {
      const { name, hasStateChanged } = payload;
      return {
        ...state,
        [name]: !state[name as keyof BuilderState],
        hasStateChanged: hasStateChanged != null ? hasStateChanged : true,
      };
    }

    case LOAD_SAVED_AUDIENCE: {
      const { initialLoadState } = payload;
      return {
        ...initialState,
        audienceId: initialLoadState.audienceId ?? null,
        audienceName: initialLoadState.audienceName ?? initialState.audienceName,
        hasStateChanged:
          initialLoadState.hasStateChanged != null
            ? initialLoadState.hasStateChanged
            : false,
        hasGottenCounts:
          initialLoadState.hasGottenCounts != null
            ? initialLoadState.hasGottenCounts
            : false,
        isSavedAudience:
          initialLoadState.isSavedAudience != null
            ? initialLoadState.isSavedAudience
            : true,
        recordsAvailable: initialLoadState.recordsAvailable || 0,
        recordsIncluded:
          initialLoadState.incomingRecordsIncluded ||
          initialLoadState.recordsAvailable ||
          0,
        incomingRecordsIncluded:
          initialLoadState.incomingRecordsIncluded ||
          initialLoadState.recordsAvailable ||
          0,
        // The loader (convertFiltersToBuilderFormat) hands us trees
        // directly. PR 2c: trees include audience/file membership leaves
        // alongside variable leaves, so the dropped myDataInclude /
        // myDataExclude arrays are baked into the trees by the loader.
        // Falsy → leave as initialState's null.
        expression: initialLoadState.expression ?? initialState.expression,
        excludeExpression:
          initialLoadState.excludeExpression ?? initialState.excludeExpression,
        emailsAvailable: initialLoadState.emailsAvailable || 0,
        includeOnlyOneRecordPerHousehold:
          initialLoadState.includeOnlyOneRecordPerHousehold ||
          initialState.includeOnlyOneRecordPerHousehold,
      };
    }

    case SET_RECORDS_AVAILABLE: {
      const { recordsAvailable, emailsAvailable } = payload;
      return {
        ...state,
        recordsAvailable,
        recordsIncluded: recordsAvailable,
        incomingRecordsIncluded: recordsAvailable,
        hasStateChanged: false,
        hasGottenCounts: true,
        emailsAvailable,
      };
    }

    case SET_RECORDS_INCLUDED: {
      const { recordsIncluded } = payload;
      return { ...state, recordsIncluded };
    }

    case SET_SAVED_AUDIENCE: {
      const { audienceId, audienceName } = payload;
      return {
        ...state,
        audienceId,
        audienceName,
        hasStateChanged: false,
        hasGottenCounts: true,
        isSavedAudience: true,
      };
    }

    case SET_AUDIENCE_NAME_ERROR: {
      const { audienceNameError } = payload;
      return { ...state, audienceNameError };
    }

    case SET_ADJUSTMENT_ERROR: {
      return { ...state, adjustmentError: payload };
    }

    case RESET:
      return { ...initialState };

    case LOAD_TOUR_SAMPLE: {
      // Replace the Include expression with a pre-built tree carrying both
      // top-level AND siblings and a nested OR sub-group, so the AND/OR tour
      // has anchors for the connector pill, group bracket, and depth-color
      // demonstration. Caller must guard against overwriting real user work
      // (see useTour in components/tour).
      const { tree } = payload as { tree: ExpressionNode };
      return {
        ...state,
        expression: tree,
        hasStateChanged: true,
      };
    }

    case TOGGLE_OPERATOR: {
      const { nodeId, zoneType } = payload as {
        nodeId: string;
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      return {
        ...state,
        [exprKey]: toggleNodeOperator(state[exprKey], nodeId),
        hasStateChanged: true,
      };
    }

    case WRAP_IN_GROUP: {
      const { sourceNodeId, targetNodeId, op, zoneType } = payload as {
        sourceNodeId: string;
        targetNodeId: string;
        op: 'and' | 'or';
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      const next = wrapNodesInGroup(
        state[exprKey],
        sourceNodeId,
        targetNodeId,
        op,
      );
      // No-op shortcut: if the helper returned the same tree (invalid wrap),
      // don't flip hasStateChanged — nothing actually changed.
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case UNGROUP: {
      const { nodeId, zoneType } = payload as {
        nodeId: string;
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      const next = ungroupById(state[exprKey], nodeId);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case ADD_ATTRIBUTE_AND_WRAP: {
      const { id, targetNodeId, op, zoneType } = payload as {
        id: string;
        targetNodeId: string;
        op: 'and' | 'or';
        zoneType: ZoneType;
      };
      const attribute = getAttributeById(dataDictionary, id) as VariableState | undefined;
      if (!attribute) {
        console.warn('[builderReducer] attributeToAdd is undefined for id:', id);
        return state;
      }
      const exprKey = expressionKeyFor(zoneType);
      const newLeaf = createLeaf(attribute);
      const next = wrapNodeWithFreshLeaf(state[exprKey], targetNodeId, newLeaf, op);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case ADD_LEAF_TO_GROUP: {
      const { id, groupNodeId, zoneType } = payload as {
        id: string;
        groupNodeId: string;
        zoneType: ZoneType;
      };
      const attribute = getAttributeById(dataDictionary, id) as VariableState | undefined;
      if (!attribute) {
        console.warn('[builderReducer] attributeToAdd is undefined for id:', id);
        return state;
      }
      const exprKey = expressionKeyFor(zoneType);
      const newLeaf = createLeaf(attribute);
      const next = addLeafToGroup(state[exprKey], groupNodeId, newLeaf);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case MOVE_LEAF_INTO_GROUP: {
      const { sourceNodeId, groupNodeId, zoneType } = payload as {
        sourceNodeId: string;
        groupNodeId: string;
        zoneType: ZoneType;
      };
      const exprKey = expressionKeyFor(zoneType);
      const next = moveLeafIntoGroup(state[exprKey], sourceNodeId, groupNodeId);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case ADD_MY_DATA_AND_WRAP: {
      const { id, name, type: resourceType, targetNodeId, op, zoneType } =
        payload as {
          id: string;
          name: string;
          type: MembershipResourceType;
          targetNodeId: string;
          op: 'and' | 'or';
          zoneType: ZoneType;
        };
      const exprKey = expressionKeyFor(zoneType);
      const newLeaf = membershipLeafOrNull(resourceType, { id, name });
      if (!newLeaf) return state;
      const next = wrapNodeWithFreshLeaf(state[exprKey], targetNodeId, newLeaf, op);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    case ADD_MY_DATA_TO_GROUP: {
      const { id, name, type: resourceType, groupNodeId, zoneType } =
        payload as {
          id: string;
          name: string;
          type: MembershipResourceType;
          groupNodeId: string;
          zoneType: ZoneType;
        };
      const exprKey = expressionKeyFor(zoneType);
      const newLeaf = membershipLeafOrNull(resourceType, { id, name });
      if (!newLeaf) return state;
      const next = addLeafToGroup(state[exprKey], groupNodeId, newLeaf);
      if (next === state[exprKey]) return state;
      return {
        ...state,
        [exprKey]: next,
        hasStateChanged: true,
      };
    }

    default:
      return state;
  }
};

// `parseId` is re-exported here (was used by the original REMOVE_CATEGORY but
// is still imported by other files via builderReducer in some places).
export { parseId };
